import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { Fingerprint, Scan, Mail, Lock, LogIn, Building2, Shield } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

export const LoginPage = () => {
  const navigate = useNavigate();
  const { setIsAuthenticated, setCurrentUser } = useAppContext();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  // Mock user for demo
  const mockUser = {
    id: "1",
    name: "Dr. Mario Rossi",
    email: "mario.rossi@farmacia.it",
    pharmacy: "Farmacia Centrale",
    role: "pharmacist"
  };

  const handleLogin = async (method) => {
    setIsLoading(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setIsAuthenticated(true);
    setCurrentUser(mockUser);
    setIsLoading(false);
    
    toast.success("Accesso effettuato!", {
      description: `Benvenuto, ${mockUser.name}`
    });
    
    navigate("/");
  };

  const handleEmailLogin = (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Compila tutti i campi");
      return;
    }
    handleLogin("email");
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex flex-col">
      {/* Hero section */}
      <div className="flex-1 flex flex-col items-center justify-center px-4 py-8 md:py-12">
        {/* Logo */}
        <div className="mb-8 animate-fade-in-up">
          <img 
            src={LOGO_URL} 
            alt="WitApp" 
            className="h-16 md:h-20 w-auto"
          />
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md animate-fade-in-up" style={{ animationDelay: "100ms" }}>
          <CardHeader className="text-center pb-2">
            <CardTitle className="text-2xl">Witapp e-Pharma</CardTitle>
            <CardDescription className="text-base">
              Accedi per gestire le prestazioni sanitarie
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Biometric/OAuth buttons */}
            <div className="space-y-3">
              <Button
                variant="premium"
                size="lg"
                className="w-full gap-3"
                onClick={() => handleLogin("biometric")}
                disabled={isLoading}
                data-testid="login-biometric-btn"
              >
                <Fingerprint className="h-5 w-5" />
                Accesso Biometrico
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="w-full gap-3"
                onClick={() => handleLogin("spid")}
                disabled={isLoading}
                data-testid="login-spid-btn"
              >
                <Shield className="h-5 w-5" />
                Accedi con SPID
              </Button>
              
              <Button
                variant="outline"
                size="lg"
                className="w-full gap-3"
                onClick={() => handleLogin("cie")}
                disabled={isLoading}
                data-testid="login-cie-btn"
              >
                <Scan className="h-5 w-5" />
                Accedi con CIE
              </Button>
            </div>

            <div className="relative">
              <Separator />
              <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-card px-2 text-xs text-muted-foreground">
                oppure
              </span>
            </div>

            {/* Email login form */}
            <form onSubmit={handleEmailLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="nome@farmacia.it"
                    className="pl-10"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={isLoading}
                    data-testid="login-email-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    className="pl-10"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={isLoading}
                    data-testid="login-password-input"
                  />
                </div>
              </div>

              <Button
                type="submit"
                variant="secondary"
                size="lg"
                className="w-full gap-2"
                disabled={isLoading}
                data-testid="login-email-submit-btn"
              >
                {isLoading ? (
                  <div className="h-5 w-5 animate-spin rounded-full border-2 border-foreground border-t-transparent" />
                ) : (
                  <LogIn className="h-5 w-5" />
                )}
                Accedi
              </Button>
            </form>

            <p className="text-center text-xs text-muted-foreground">
              Accedendo accetti i{" "}
              <a href="#" className="text-primary hover:underline">Termini di Servizio</a>
              {" "}e la{" "}
              <a href="#" className="text-primary hover:underline">Privacy Policy</a>
            </p>
          </CardContent>
        </Card>

        {/* Trust indicators */}
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-muted-foreground animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          <div className="flex items-center gap-2">
            <Shield className="h-4 w-4 text-primary" />
            <span className="text-xs">GDPR Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="h-4 w-4 text-primary" />
            <span className="text-xs">NIS2 Compliant</span>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="py-4 text-center text-xs text-muted-foreground">
        <p>© 2024 WitApp. Tutti i diritti riservati.</p>
      </footer>
    </div>
  );
};

export default LoginPage;